export const endpoints = {
  auth: {
    loginRedirect: 'auth/microsoft/redirect?surface=backoffice',
    exchangeCode: 'auth/exchange-code',
    refresh: 'auth/refresh',
    logout: 'auth/logout',
  },
  backoffice: {
    me: 'backoffice/me',
    impersonate: 'backoffice/impersonate',

    organizations: 'backoffice/organizations',
    organizationsExport: 'backoffice/organizations/export',
    organization: (organization: string) => `backoffice/organizations/${organization}`,
    organizationOverview: (organization: string) =>
      `backoffice/organizations/${organization}/overview`,
    organizationBillingEmail: (organization: string) =>
      `backoffice/organizations/${organization}/billing-email`,
    organizationSuspend: (organization: string) =>
      `backoffice/organizations/${organization}/suspend`,
    organizationReactivate: (organization: string) =>
      `backoffice/organizations/${organization}/reactivate`,
    organizationRoot: (organization: string) => `backoffice/organizations/${organization}/root`,

    organizationNotes: (organization: string) => `backoffice/organizations/${organization}/notes`,
    organizationNote: (organization: string, note: string) =>
      `backoffice/organizations/${organization}/notes/${note}`,

    organizationSubscription: (organization: string) =>
      `backoffice/organizations/${organization}/subscription`,
    organizationSubscriptionPreview: (organization: string) =>
      `backoffice/organizations/${organization}/subscription/preview-change`,
    organizationSubscriptionUpdate: (organization: string) =>
      `backoffice/organizations/${organization}/subscription/update`,
    organizationSubscriptionPause: (organization: string) =>
      `backoffice/organizations/${organization}/subscription/pause`,
    organizationSubscriptionResume: (organization: string) =>
      `backoffice/organizations/${organization}/subscription/resume`,
    organizationSubscriptionCancel: (organization: string) =>
      `backoffice/organizations/${organization}/subscription/cancel`,
    organizationSubscriptionTrial: (organization: string) =>
      `backoffice/organizations/${organization}/subscription/trial`,
    organizationSubscriptionItemOverrides: (organization: string, item: string) =>
      `backoffice/organizations/${organization}/subscription/items/${item}/overrides`,
    organizationSubscriptionOverrideRevert: (
      organization: string,
      item: string,
      override: string,
    ) =>
      `backoffice/organizations/${organization}/subscription/items/${item}/overrides/${override}/revert`,

    organizationInvoices: (organization: string) =>
      `backoffice/organizations/${organization}/invoices`,
    organizationInvoice: (organization: string, invoice: string) =>
      `backoffice/organizations/${organization}/invoices/${invoice}`,
    organizationInvoicePdf: (organization: string, invoice: string) =>
      `backoffice/organizations/${organization}/invoices/${invoice}/pdf`,
    organizationInvoiceResend: (organization: string, invoice: string) =>
      `backoffice/organizations/${organization}/invoices/${invoice}/resend`,
    organizationInvoiceUncollectible: (organization: string, invoice: string) =>
      `backoffice/organizations/${organization}/invoices/${invoice}/mark-uncollectible`,
    organizationInvoiceCreditNote: (organization: string, invoice: string) =>
      `backoffice/organizations/${organization}/invoices/${invoice}/credit-note`,
    organizationPayments: (organization: string) =>
      `backoffice/organizations/${organization}/payments`,
    organizationPaymentsAttention: (organization: string) =>
      `backoffice/organizations/${organization}/payments/attention`,
    organizationPaymentRetry: (organization: string, payment: string) =>
      `backoffice/organizations/${organization}/payments/${payment}/retry`,
    organizationPaymentRefund: (organization: string, payment: string) =>
      `backoffice/organizations/${organization}/payments/${payment}/refund`,
    organizationRefunds: (organization: string) =>
      `backoffice/organizations/${organization}/refunds`,
    organizationCreditNotes: (organization: string) =>
      `backoffice/organizations/${organization}/credit-notes`,

    organizationLicences: (organization: string) =>
      `backoffice/organizations/${organization}/licences`,
    organizationLicencesFreePool: (organization: string) =>
      `backoffice/organizations/${organization}/licences/free-pool`,
    organizationLicencesAdjust: (organization: string) =>
      `backoffice/organizations/${organization}/licences/adjust`,
    organizationLicencesResume: (organization: string) =>
      `backoffice/organizations/${organization}/licences/resume`,
    organizationLicenceAssign: (organization: string, licence: string) =>
      `backoffice/organizations/${organization}/licences/${licence}/assign`,
    organizationLicenceRelease: (organization: string, licence: string) =>
      `backoffice/organizations/${organization}/licences/${licence}/release`,
    organizationLicenceCancel: (organization: string, licence: string) =>
      `backoffice/organizations/${organization}/licences/${licence}/cancel`,
    organizationLicenceResume: (organization: string, licence: string) =>
      `backoffice/organizations/${organization}/licences/${licence}/resume`,

    organizationDevices: (organization: string) =>
      `backoffice/organizations/${organization}/devices`,
    organizationDevice: (organization: string, device: string) =>
      `backoffice/organizations/${organization}/devices/${device}`,

    reconciliation: 'backoffice/reconciliation',
    reconciliationReports: 'backoffice/reconciliation/reports',
    reconciliationRun: 'backoffice/reconciliation/run',
    organizationReconciliation: (organization: string) =>
      `backoffice/organizations/${organization}/reconciliation`,
    organizationReconciliationRun: (organization: string) =>
      `backoffice/organizations/${organization}/reconciliation/run`,

    webhookEvents: 'backoffice/webhook-events',
    webhookEvent: (event: string) => `backoffice/webhook-events/${event}`,
    webhookEventReplay: (event: string) => `backoffice/webhook-events/${event}/replay`,

    auditLogs: 'backoffice/audit-logs',

    opsHealth: 'backoffice/ops/health',
    opsFailedJobs: 'backoffice/ops/failed-jobs',
    opsFailedJobsRetry: 'backoffice/ops/failed-jobs/retry',
    opsFailedJobsRetryAll: 'backoffice/ops/failed-jobs/retry-all',

    settings: 'backoffice/settings',
    onboardings: 'backoffice/onboardings',
    organizationTenantHealthCheck: (organization: string) =>
      `backoffice/organizations/${organization}/tenant-health/check`,

    users: 'backoffice/users',
    user: (user: string) => `backoffice/users/${user}`,
    userSystemRole: (user: string) => `backoffice/users/${user}/system-role`,
    systemUsers: 'backoffice/system-users',

    plans: 'backoffice/plans',
    plan: (plan: string) => `backoffice/plans/${plan}`,
    planFeatures: (plan: string) => `backoffice/plans/${plan}/features`,
    planPrices: (plan: string) => `backoffice/plans/${plan}/prices`,
    planPrice: (plan: string, price: string) => `backoffice/plans/${plan}/prices/${price}`,
    planSubscribers: (plan: string) => `backoffice/plans/${plan}/subscribers`,
    features: 'backoffice/features',
    markets: 'backoffice/markets',

    rbacRoles: 'backoffice/rbac/roles',
    rbacRole: (role: string) => `backoffice/rbac/roles/${role}`,
    rbacPermissions: 'backoffice/rbac/permissions',
    rbacUserScopes: (user: string) => `backoffice/rbac/users/${user}/permission-scopes`,
  },
};
